import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import { translate } from '../../../services/translate';
import { useAuth } from '../../../context/Auth';
import { DashBoardParamList } from '../../../typings/Params';
import PlateGroupDropdown from '../../../components/dashboard/PlateGroupDropdown';
import PeriodDropdown, { PeriodType } from '../../../components/deac/PeriodDropdown';
import moment from 'moment';
import { View } from 'react-native';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import MyAppText from '../../../components/MyAppText';

interface Props {
    filters: DashBoardParamList['DashboardPlateMonitoring'];
    setFilters: React.Dispatch<React.SetStateAction<DashBoardParamList['DashboardPlateMonitoring']>>;
    groupId: number;
    setGroupId: React.Dispatch<React.SetStateAction<number>>;
    onStatusChange?: (isOpen: boolean) => void;
}

export default function Filter({ filters, setFilters, groupId, setGroupId, onStatusChange }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<DashBoardParamList, 'DashboardPlateMonitoring'>>>();
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    if (!authenticatedUser) {
        return <></>;
    }

    const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>(filters.fixedPeriod ?? 'weekly');
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.fixedPeriod != 'select' ? null : filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.fixedPeriod != 'select' ? '' : filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.fixedPeriod != 'select' ? null : filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.fixedPeriod != 'select' ? '' : filters.endIn ? moment(filters.endIn).format('HH:mm') : '');

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const handleCleanFilter = () => {
        setSelectedPeriod('weekly');
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
    };

    const formatDateTime = (date: Date | null, time: string) =>
        date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss') : undefined;

    function endInErrorsMessages() {
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        if (beginIn && !endIn) {
            return translate('required_field');
        } else if (beginIn && endIn) {
            if (!isBeginBeforeEnd(beginIn, endIn)) {
                return translate('invertedDates');
            } else if (!isDiffInDaysLowerThan31(beginIn, endIn)) {
                return translate('maxPeriodOfXDays', { days: 31 });
            }
        }
        return null;
    }

    function beginInErrorsMessages() {
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        if (!beginIn && endIn) {
            return translate('required_field');
        } else if (beginIn && endIn) {
            if (!isBeginBeforeEnd(beginIn, endIn)) {
                return translate('invertedDates');
            } else if (!isDiffInDaysLowerThan31(beginIn, endIn)) {
                return translate('maxPeriodOfXDays', { days: 31 });
            }
        }
        return null;
    }

    function isBeginBeforeEnd(beginIn: moment.Moment, endIn: moment.Moment) {
        return beginIn.isBefore(endIn);
    }

    function isDiffInDaysLowerThan31(beginIn: moment.Moment, endIn: moment.Moment) {
        return endIn.diff(beginIn, 'days') <= 31;
    }

    function isValidFilters() {
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        return groupId != 0 && (selectedPeriod != 'select' || (beginIn && endIn && isBeginBeforeEnd(beginIn, endIn) && isDiffInDaysLowerThan31(beginIn, endIn)));
    }

    const handleFilter = () => {
        if (!isValidFilters()) {
            return;
        }

        let beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00')?.valueOf();
        let endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59')?.valueOf();

        if (selectedPeriod != 'select') {
            beginIn = undefined;
            endIn = moment().valueOf();

            if (selectedPeriod === 'lastHour') {
                beginIn = moment().subtract(1, 'hour').valueOf();
            } else if (selectedPeriod === 'last12Hour') {
                beginIn = moment().subtract(12, 'hours').valueOf();
            } else if (selectedPeriod === 'last24Hours') {
                beginIn = moment().subtract(24, 'hours').valueOf();
            } else if (selectedPeriod === 'weekly') {
                beginIn = moment().subtract(1, 'week').valueOf();
            } else if (selectedPeriod === 'fortnightly') {
                beginIn = moment().subtract(2, 'weeks').valueOf();
            } else if (selectedPeriod === 'monthly') {
                beginIn = moment().subtract(30, 'days').valueOf();
            }
        }

        navigation.setParams({
            ...filters,
            groupId: groupId,
            fixedPeriod: selectedPeriod != 'allPeriod' ? selectedPeriod : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
        });

        setFilters({
            ...filters,
            groupId: groupId != 0 ? groupId : undefined,
            fixedPeriod: selectedPeriod != 'allPeriod' ? selectedPeriod : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
        });
    };

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            onStatusChange={onStatusChange}
            applyDisabled={!isValidFilters()}
        >
            <PlateGroupDropdown
                value={groupId}
                setValue={setGroupId}
                zIndex={4}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />
            {groupId === 0
                ? <MyAppText style={styles.errorMessage}>{translate('required_field')}</MyAppText>
                : null
            }

            <PeriodDropdown
                label={translate('period')}
                value={selectedPeriod}
                setValue={(val) => {
                    setBeginInDateFilter(null);
                    setBeginInHourFilter('');
                    setEndInDateFilter(null);
                    setEndInHourFilter('');
                    setSelectedPeriod(val);
                }}
                zIndex={3}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
                periods={[
                    'lastHour',
                    'last12Hour',
                    'last24Hours',
                    'weekly',
                    'fortnightly',
                    'monthly',
                ]}
            />

            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={(date) => {
                        setSelectedPeriod('select');
                        setBeginInDateFilter(date);
                    }}
                    time={beginInHourFilter}
                    onChangeTime={(time) => {
                        setSelectedPeriod('select');
                        setBeginInHourFilter(time);
                    }}
                    label={translate('beginIn')}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(2)}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            </View>
            {beginInErrorsMessages()
                ? <MyAppText style={styles.errorMessage}>{beginInErrorsMessages()}</MyAppText>
                : null
            }

            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={(date) => {
                        setSelectedPeriod('select');
                        setEndInDateFilter(date);
                    }}
                    time={endInHourFilter}
                    onChangeTime={(time) => {
                        setSelectedPeriod('select');
                        setEndInHourFilter(time);
                    }}
                    label={translate('endIn')}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(1)}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            </View>
            {endInErrorsMessages()
                ? <MyAppText style={styles.errorMessage}>{endInErrorsMessages()}</MyAppText>
                : null
            }
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    selectLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    errorMessage: {
        color: theme.colors.errors['300'],
        fontWeight: '500',
        fontSize: 12,
    }
}));
